import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import config from '~/Config';
import { getPricing, planMatchesIap } from '~/Config/Stripe';
import { subscribe, updateField } from '~/Redux/Modules/Subscription';
import Button from '~/Components/Global/Form/Button';
import StripePermittedView from '~/Components/Global/StripePermittedView';
import Spinner from '~/Components/Global/Loading/Spinner';
import { parseErrors } from '~/Redux/Helpers';
import { guid, useMountEffect, usePaddle } from '~/util';
import { UserApi } from '~/Providers/Api';
import moment from '~/Providers/Moment';
import { DATE_FORMAT } from '~/Redux/Constants';
import useSetupIntent from '~/Components/Global/Stripe/useSetupIntent';
import Storage from '~/Providers/Storage';
import SetupIntentForm from '~/Components/Pages/Subscription/SetupIntentForm';

import SwapToStripe from './SwapToStripe';
import SwapToPaddle from './SwapToPaddle';

import './ManageSubscription.scss';

const SwapToWeb = () => {
  const dispatch = useDispatch();
  const account = useSelector(({ Account }) => Account.data);
  const pricingPlans = useCallback(getPricing(), []);
  const [selectedPlan, setSelectedPlan] = useState(pricingPlans.yearlyPlus);
  const {
    isFetching,
    data: { coupon },
  } = useSelector(({ Subscription }) => Subscription);
  const [errorMessage, setErrorMessage] = useState('');
  const [historicalPlans, setHistoricalPlans] = useState([]);
  const { setupIntent, error: setupIntentError, setSetupIntentId } = useSetupIntent();
  const [postalCode, setPostalCode] = useState('');
  const [eventId, setEventId] = useState(Storage.get('subscriptionEventId'));
  const usePaddleValue = usePaddle();

  const platformNames = {
    ios: 'Apple',
    android: 'Google Play',
  };

  useMountEffect(() => {
    if (!eventId) {
      const newId = guid();
      Storage.set('subscriptionEventId', newId);
      setEventId(newId);
    }
  });

  useMountEffect(() => {
    UserApi.historicalPlans()
      .then(({ data }) => {
        const plans = data
          .map(plan => {
            return Object.keys(pricingPlans).find(
              key => pricingPlans[key].plan === plan || planMatchesIap(pricingPlans[key], plan)
            );
          })
          .filter(plan => plan && ['yearly', 'yearlyPlus'].indexOf(plan) === -1);
        setHistoricalPlans(plans);
      })
      .catch(err => {
        console.error('Error fetching historical plans:', err);
      });
  });

  const activeSubscriptionSku = account?.iapSubscription?.sku;

  useEffect(() => {
    const activePlan = Object.values(pricingPlans).find(plan =>
      planMatchesIap(plan, activeSubscriptionSku)
    );
    setSelectedPlan(activePlan);
  }, [activeSubscriptionSku, pricingPlans, setSelectedPlan]);

  const handleFieldChanged = useCallback(
    change => {
      if (change.postal_code) {
        setPostalCode(change.postal_code);
      }
      dispatch(updateField(change));
    },
    [dispatch, setPostalCode]
  );

  const handleSave = () => {
    setErrorMessage('');
    dispatch(
      subscribe({
        paymentMethod: setupIntent.payment_method,
        coupon,
        plan: selectedPlan.plan,
        price: selectedPlan.price,
        userId: account.id,
        eventId,
        postalCode,
        iap_subscription_id: account?.iapSubscription?.id,
      })
    ).catch(err => {
      setErrorMessage(parseErrors(err));
    });
  };

  return (
    <div className="swap-to-web">
      <StripePermittedView>
        <h1>Access Your Grandfathered Plan</h1>
        <p className="text-center">
          You are currently subscribed through {platformNames[account?.iapSubscription?.platform]}.{' '}
          <b>Be sure to cancel your current subscription.</b>
        </p>
        <p className="text-center">
          You can swap to a web subscription of any of the plans listed below.
        </p>
        <p className="text-center">
          Your subscription will be billed when the current{' '}
          {platformNames[account?.iapSubscription?.platform]} subscription ends on{' '}
          <b>
            {moment(account?.iapSubscription?.ends_at)
              .local()
              .format(DATE_FORMAT)}
          </b>
          .
        </p>

        {usePaddleValue ? (
          <SwapToPaddle
            selectedPlan={selectedPlan}
            isFetching={isFetching}
            account={account}
            handleSave={handleSave}
            errorMessage={errorMessage}
            eventId={eventId}
            pricingPlans={pricingPlans}
            historicalPlans={historicalPlans}
            onPlanSelect={setSelectedPlan}
          />
        ) : (
          <SwapToStripe
            setupIntent={setupIntent}
            setupIntentError={setupIntentError}
            setSetupIntentId={setSetupIntentId}
            selectedPlan={selectedPlan}
            isFetching={isFetching}
            account={account}
            coupon={coupon}
            handleFieldChanged={handleFieldChanged}
            postalCode={postalCode}
            handleSave={handleSave}
            errorMessage={errorMessage}
            eventId={eventId}
            pricingPlans={pricingPlans}
            historicalPlans={historicalPlans}
            onPlanSelect={setSelectedPlan}
          />
        )}
      </StripePermittedView>
    </div>
  );
};

SwapToWeb.propTypes = {};

export default SwapToWeb;
